/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { EventHandler, IWebSocketClient, Subscription, WebsocketProviders } from './types';
import { WebSocketClientFactory } from './websocketClientFactory';
import { getConfig } from '@utils/configs';

interface IAbstractEventSubscriber {
  initialize(websocketNameSpace: string, websocketUrl: string, authUrl: string): void;
  subscribeToEvent<T>(url: string, handler: EventHandler<T>): Subscription | undefined;
}

export class EventSubscriber implements IAbstractEventSubscriber {
  private webSocketClient: IWebSocketClient;

  constructor(webSocketClient: IWebSocketClient) {
    this.webSocketClient = webSocketClient;
  }

  initialize(websocketNameSpace: string, websocketUrl: string, authUrl: string): void {
    this.webSocketClient.initialize(websocketNameSpace, websocketUrl, authUrl);
  }

  subscribeToEvent<T>(channelUrl: string, handler: EventHandler<T>): Subscription | undefined {
    return this.webSocketClient.subscribe<T>(channelUrl, handler);
  }
}

// Singleton instance of the event subscriber
let eventSubscriber: IAbstractEventSubscriber | null = null;

export const initializeWebsocketEventSubscriber = (
  websocketProvider: WebsocketProviders,
  roomId: string,
  isBorrower?: boolean,
): void => {
  const webSocketNameSpace = 'ron';
  if (!eventSubscriber) {
    const authUrl = isBorrower
      ? `/api/internal/rooms/${roomId}/websocket`
      : `/api/internal/notary/rooms/${roomId}/websocket`;
    const webSocketClient = WebSocketClientFactory.createClient(websocketProvider);
    eventSubscriber = new EventSubscriber(webSocketClient);
    eventSubscriber.initialize(webSocketNameSpace, getConfig().websocket.url, authUrl);
  }
};

export const getEventSubscriber = (): IAbstractEventSubscriber => {
  if (!eventSubscriber) {
    throw new Error('EventSubscriber not initialized.');
  }
  return eventSubscriber;
};
